import React, { useEffect, useState } from "react";
import axios from "axios";

import liveSocket from "../../lib/liveSocket";
import PresencePanel from "../../components/live/PresencePanel";
import DispatchFeed from "../../components/live/DispatchFeed";
import EmergencyHistoryPanel from "../../components/live/EmergencyHistoryPanel";

let panicAudioCtx = null;

function getPanicAudioContext() {
  const AudioContext = window.AudioContext || window.webkitAudioContext;

  if (!AudioContext) return null;

  if (!panicAudioCtx) {
    panicAudioCtx = new AudioContext();
  }

  if (panicAudioCtx.state === "suspended") {
    panicAudioCtx.resume();
  }

  return panicAudioCtx;
}

function playPanicTone() {
  try {
    const ctx = getPanicAudioContext();

    if (!ctx) return;

    const beep = (delay, frequency, duration = 0.4) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = "square";
      oscillator.frequency.value = frequency;

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + duration);

      oscillator.start(ctx.currentTime + delay);
      oscillator.stop(ctx.currentTime + delay + duration + 0.05);
    };

    beep(0, 880);
    beep(0.45, 880);
    beep(0.9, 1040);
  } catch (err) {
    console.warn("Panic tone blocked or unavailable", err);
  }
}


function EmergencyAuditTrail({ alert }) {
  const history = alert?.workflow_history || [];

  if (!alert?.alert_id || !history.length) return null;

  const labels = {
    panic_triggered: "PANIC TRIGGERED",
    control_room_acknowledged: "ACKNOWLEDGED",
    response_commenced: "RESPONSE COMMENCED",
    emergency_resolved: "RESOLVED",
  };

  const formatAuditTime = (value) => {
    if (!value) return "--:--:--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--:--:--";
    return date.toLocaleTimeString("en-AU", {
      timeZone: "Australia/Sydney",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  return (
    <div className="rounded-xl border border-red-500/30 bg-black/40 p-4">
      <div className="text-xs font-black uppercase tracking-[0.2em] text-red-300 mb-3">
        Emergency Audit Trail
      </div>

      <div className="space-y-3">
        {history.map((item, index) => (
          <div
            key={`${item.event || "event"}-${item.timestamp || index}`}
            className="grid grid-cols-[76px_1fr] gap-3 text-sm"
          >
            <div className="font-mono text-zinc-400">
              {formatAuditTime(item.timestamp)}
            </div>

            <div>
              <div className="font-bold text-white">
                {labels[item.event] || (item.status || item.event || "EVENT").toUpperCase()}
              </div>

              {item.actor_name && (
                <div className="text-zinc-300">
                  {item.actor_name}
                </div>
              )}

              {item.note && (
                <div className="text-zinc-400 mt-1 whitespace-pre-wrap">
                  {item.note}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function formatEmergencySlaClock(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return "—";

  const value = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(value / 60);
  const secs = value % 60;

  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function EmergencySlaBanner({ alert }) {
  const [slaNow, setSlaNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(
      () => setSlaNow(Date.now()),
      1000
    );

    return () => window.clearInterval(timer);
  }, []);

  if (!alert) return null;

  const triggeredMs = new Date(
    alert.timestamp ||
    alert.created_at ||
    alert.triggered_at
  ).getTime();

  if (!Number.isFinite(triggeredMs)) return null;

  const elapsedSeconds = Math.max(
    0,
    (slaNow - triggeredMs) / 1000
  );

  const acknowledged = Boolean(alert.acknowledged_at);
  const responding = Boolean(alert.responding_at);

  const ackRemaining = 120 - elapsedSeconds;
  const responseRemaining = 300 - elapsedSeconds;

  let state = "WITHIN SLA";
  let detail = acknowledged
    ? "Acknowledgement completed"
    : `Acknowledge within ${formatEmergencySlaClock(ackRemaining)}`;

  let tone =
    "border-emerald-500/40 bg-emerald-950/30 text-emerald-100";

  if (!responding && elapsedSeconds > 600) {
    state = "SUPERVISOR ESCALATION";
    detail =
      "Response is critically overdue — supervisor escalation required";
    tone =
      "border-red-500 bg-red-950/60 text-red-100";
  } else if (!responding && elapsedSeconds > 300) {
    state = "RESPONSE OVERDUE";
    detail = "Response commencement SLA breached";
    tone =
      "border-red-500/70 bg-red-950/40 text-red-100";
  } else if (!acknowledged && elapsedSeconds > 120) {
    state = "ACKNOWLEDGEMENT OVERDUE";
    detail = "Acknowledgement SLA breached";
    tone =
      "border-amber-500/70 bg-amber-950/40 text-amber-100";
  } else if (acknowledged && !responding) {
    state = "ACKNOWLEDGED";
    detail =
      `Response commencement within ${formatEmergencySlaClock(responseRemaining)}`;
    tone =
      "border-amber-500/40 bg-amber-950/20 text-amber-100";
  } else if (responding) {
    state = "RESPONDING";
    detail = "Control Room response commenced";
    tone =
      "border-sky-500/40 bg-sky-950/30 text-sky-100";
  }

  return (
    <div className={`mb-4 rounded-xl border px-4 py-3 ${tone}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70">
            V4.0E-2J Emergency SLA
          </div>

          <div className="font-black mt-1">
            {state}
          </div>

          <div className="text-sm mt-1 opacity-90">
            {detail}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase opacity-60">
              Ack SLA
            </div>
            <div className="font-mono font-black">
              {acknowledged
                ? "DONE"
                : formatEmergencySlaClock(ackRemaining)}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase opacity-60">
              Response SLA
            </div>
            <div className="font-mono font-black">
              {responding
                ? "DONE"
                : formatEmergencySlaClock(responseRemaining)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PanicBanner({
  alerts,
  audit,
  onAcknowledge,
  onResponding,
  onResolve
}) {
  if (!alerts.length) return null;

  const alert = alerts[0];
  const status = alert.status || "active";

  return (
    <div className="mb-6 rounded-2xl border-2 border-red-400 bg-red-950/80 shadow-2xl shadow-red-900/60 p-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="text-red-200 text-xs font-black uppercase tracking-[0.25em]">
            🚨 Active Panic Alert
          </div>

          <h2 className="text-2xl font-black text-white mt-2">
            {alert.officer_name || "Officer"} needs urgent assistance
          </h2>

          <div className="text-red-100 mt-1">
            {alert.site_name || "Unknown site"}
          </div>

          <div className="text-red-200/80 text-sm mt-2">
            Alert ID: {alert.alert_id}
          </div>

          <div className="mt-3">
            <span className={
              status === "responding"
                ? "inline-flex rounded-full px-3 py-1 text-xs font-black uppercase bg-yellow-400/20 text-yellow-200 border border-yellow-400/50"
                : status === "acknowledged"
                ? "inline-flex rounded-full px-3 py-1 text-xs font-black uppercase bg-amber-400/20 text-amber-200 border border-amber-400/50"
                : "inline-flex rounded-full px-3 py-1 text-xs font-black uppercase bg-red-400/20 text-red-200 border border-red-400/50"
            }>
              {status}
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">

          {status === "active" && (
            <button
              onClick={() => onAcknowledge(alert.alert_id)}
              className="px-6 py-4 rounded-xl bg-white text-red-700 font-black hover:bg-red-100 active:scale-[0.98]"
            >
              ACKNOWLEDGE EMERGENCY
            </button>
          )}

          {status === "acknowledged" && (
            <button
              onClick={() => onResponding(alert.alert_id)}
              className="px-6 py-4 rounded-xl bg-amber-500 text-black font-black hover:bg-amber-400 active:scale-[0.98]"
            >
              MARK RESPONDING
            </button>
          )}

          {status === "responding" && (
            <button
              onClick={() => onResolve(alert.alert_id)}
              className="px-6 py-4 rounded-xl bg-green-600 text-white font-black hover:bg-green-700 active:scale-[0.98]"
            >
              RESOLVE EMERGENCY
            </button>
          )}

        </div>
      </div>

      {audit && (
        <div className="mt-4">
          <EmergencyAuditTrail alert={audit} />
        </div>
      )}
    </div>
  );
}

function authHeaders() {
  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("token");

  return token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};
}

export default function LiveOperationsPage() {
  const [presence, setPresence] = useState([]);
  const [events, setEvents] = useState([]);
  const [activePanicAlerts, setActivePanicAlerts] = useState([]);
  const [panicAudit, setPanicAudit] = useState(null);

  const loadActivePanics = () => {
    axios
      .get("/api/panic/active", authHeaders())
      .then((res) => {
        setActivePanicAlerts(res.data.alerts || []);
      })
      .catch(console.error);
  };

  const acknowledgePanic = async (alertId) => {
    if (!alertId) return;

    try {
      await axios.post(`/api/panic/${alertId}/acknowledge`, {}, authHeaders());

      setActivePanicAlerts((prev) =>
        prev.map((a) =>
          a.alert_id === alertId
            ? { ...a, status: "acknowledged" }
            : a
        )
      );
    } catch (err) {
      console.error("Failed to acknowledge panic", err);
      alert("Failed to acknowledge panic alert");
    }
  };

  const markPanicResponding = async (alertId) => {
    if (!alertId) return;

    try {
      const res = await axios.post(
        `/api/panic/${alertId}/responding`,
        {},
        authHeaders()
      );

      setActivePanicAlerts((prev) =>
        prev.map((a) =>
          a.alert_id === alertId
            ? {
                ...a,
                ...(res.data || {}),
                status: "responding"
              }
            : a
        )
      );

      loadActivePanics();
    } catch (err) {
      console.error("Failed to mark panic responding", err);

      alert(
        err?.response?.data?.detail ||
        "Failed to mark emergency as responding"
      );
    }
  };

  const resolvePanic = async (alertId) => {
    if (!alertId) return;

    const resolutionNote = window.prompt(
      "Enter the emergency resolution note:"
    );

    if (resolutionNote === null) return;

    const cleanedNote = resolutionNote.trim();

    if (cleanedNote.length < 3) {
      alert("A resolution note is required.");
      return;
    }

    try {
      await axios.post(
        `/api/panic/${alertId}/resolve`,
        {
          resolution_note: cleanedNote
        },
        authHeaders()
      );

      setActivePanicAlerts((prev) =>
        prev.filter((a) => a.alert_id !== alertId)
      );
    } catch (err) {
      console.error("Failed to resolve panic", err);

      alert(
        err?.response?.data?.detail ||
        "Failed to resolve panic alert"
      );
    }
  };

  // V4.0E-2G-2 — keep audit trail synchronised with lifecycle state.
  useEffect(() => {
    const activeAlert = activePanicAlerts[0];

    if (!activeAlert?.alert_id) {
      setPanicAudit(null);
      return;
    }

    let cancelled = false;

    axios
      .get(`/api/panic/${activeAlert.alert_id}/audit`, authHeaders())
      .then((res) => {
        if (!cancelled) {
          setPanicAudit(res.data || null);
        }
      })
      .catch((err) => {
        console.error("Failed to load emergency audit trail", err);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activePanicAlerts[0]?.alert_id,
    activePanicAlerts[0]?.status
  ]);

  useEffect(() => {
    const token =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token");

    if (!token) {
      console.warn("No auth token found");
      return;
    }

    liveSocket.connect(token);

    axios
      .get("/api/live/presence")
      .then((res) => {
        setPresence(res.data.presence || []);
      })
      .catch(console.error);

    loadActivePanics();

    const unsubscribePresence = liveSocket.on(
      "presence_update",
      (data) => {
        setPresence((prev) => {
          const filtered = prev.filter((p) => p.user_id !== data.user_id);
          return [...filtered, data];
        });

        setEvents((prev) => [data, ...prev].slice(0, 100));
      }
    );

    const unsubscribeAll = liveSocket.on("*", (data) => {
      if (
        data?.type === "panic_alert" ||
        data?.category === "panic" ||
        data?.severity === "critical"
      ) {
        playPanicTone();

        const payload = data.payload || {};
        if (payload.alert_id) {
          setActivePanicAlerts((prev) => {
            const exists = prev.some((a) => a.alert_id === payload.alert_id);
            return exists ? prev : [payload, ...prev];
          });
        }
      }

      if (data?.payload?.event === "panic_acknowledged") {
        const alertId = data.payload.alert_id;
        setActivePanicAlerts((prev) =>
          prev.map((a) =>
            a.alert_id === alertId
              ? { ...a, status: "acknowledged" }
              : a
          )
        );
      }

        if (data?.payload?.event === "panic_responding") {
          const alertId = data.payload.alert_id;

          setActivePanicAlerts((prev) =>
            prev.map((a) =>
              a.alert_id === alertId
                ? {
                    ...a,
                    ...data.payload,
                    status: "responding"
                  }
                : a
            )
          );
        }

      if (data?.payload?.event === "panic_resolved") {
        const alertId = data.payload.alert_id;
        setActivePanicAlerts((prev) =>
          prev.filter((a) => a.alert_id !== alertId)
        );
      }

      setEvents((prev) => [data, ...prev].slice(0, 100));
    });

    return () => {
      unsubscribePresence();
      unsubscribeAll();
      liveSocket.disconnect();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-black p-6"
      onClick={() => getPanicAudioContext()}
      onTouchStart={() => getPanicAudioContext()}
    >
      <EmergencySlaBanner
        alert={activePanicAlerts?.[0] || null}
      />

      <PanicBanner
        alerts={activePanicAlerts}
        audit={panicAudit}
        onAcknowledge={acknowledgePanic}
        onResponding={markPanicResponding}
        onResolve={resolvePanic}
      />

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">
          Live Operations
        </h1>

        <p className="text-zinc-400 mt-2">
          Real-time operational intelligence and dispatch monitoring.
        </p>
      </div>

      <EmergencyHistoryPanel />

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-4">
          <PresencePanel presence={presence} />
        </div>

        <div className="col-span-8">
          <DispatchFeed events={events} />
        </div>
      </div>
    </div>
  );
}
